import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../utils/apiService';
import { auth } from '../firebase/config';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Always check local storage first
        const token = localStorage.getItem('token');
        const storedUser = authService.getUser();
        const storedTenant = authService.getTenant();
        
        // If we have a token and user data, set it immediately
        if (token && storedUser) {
          setCurrentUser(storedUser);
          setCurrentTenant(storedTenant);
          setLoading(false); // Set loading to false immediately to prevent UI flicker
          
          // Then try to refresh user data silently in the background
          // This is done after setting the initial state to ensure the UI is responsive
          setTimeout(async () => {
            try {
              // Use a silent fetch that won't trigger redirects
              const userData = await authService.getCurrentUser().catch(err => {
                console.log('Silent auth refresh failed, using stored credentials');
                return { user: storedUser, tenant: storedTenant };
              });
              
              if (userData && userData.user) {
                setCurrentUser(userData.user);
                setCurrentTenant(userData.tenant);
              }
            } catch (error) {
              console.error('Background refresh error:', error);
              // Keep using the stored credentials
            }
          }, 1000); // Delay the background refresh to prioritize UI rendering
          
          return; // Exit early after setting up the background refresh
        } else {
          // No token or user data, user is not logged in
          setCurrentUser(null);
          setCurrentTenant(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // On error, check if we have stored credentials and use them
        const storedUser = authService.getUser();
        const storedTenant = authService.getTenant();
        if (storedUser && localStorage.getItem('token')) {
          setCurrentUser(storedUser);
          setCurrentTenant(storedTenant);
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      setCurrentUser(response.user);
      setCurrentTenant(response.tenant);
      // After successful login, redirect to dashboard
      navigate('/dashboard');
      return response;
    } catch (error) {
      // Ensure we don't update state on failed login
      console.error('Login error in AuthContext:', error);
      throw error; // Re-throw to be handled by the component
    }
  };

  // Google login function using Firebase
  const googleLogin = async (phoneNumber = null) => {
    try {
      // First, sign out any existing user to ensure a clean auth state
      await auth.signOut();
      
      // Configure Google provider with select_account to always show the account picker
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
      });
      
      // Sign in with popup
      const result = await signInWithPopup(auth, provider);
      
      // Get the Google access token
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      const idToken = await result.user.getIdToken();
      
      // Send the token to your backend with phone number if provided
      const response = await authService.firebaseLogin({
        idToken,
        accessToken,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        phoneNumber: phoneNumber
      });
      
      // Update state with user and tenant info
      setCurrentUser(response.user);
      setCurrentTenant(response.tenant);
      
      return response;
    } catch (error) {
      console.error('Google login error in AuthContext:', error);
      
      // Check if phone number is needed before clearing auth state
      if (error.needsPhoneNumber) {
        // Don't sign out, just pass the error through
        throw error;
      }
      
      // Clear any partial authentication state
      await auth.signOut().catch(e => console.error('Error signing out after failed login:', e));
      
      // Format the error for consistent handling
      if (error.code) {
        throw { 
          code: error.code,
          message: error.message || 'Google authentication failed'
        };
      } else if (error.response) {
        throw {
          status: error.response.status,
          message: error.response.data?.message || 'Server authentication failed',
          response: error.response,
          needsPhoneNumber: error.response.data?.needsPhoneNumber
        };
      } else {
        throw error;
      }
    }
  };

  // Register tenant function
  const registerTenant = async (tenantData, userData) => {
    // Combine tenant and user data for the API
    const registrationData = {
      tenantName: tenantData.name,
      username: userData.username,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      plan: tenantData.plan || 'free'
    };
    
    const response = await authService.registerTenant(registrationData);
    setCurrentUser(response.user);
    setCurrentTenant(response.tenant);
    return response;
  };

  // Register user function (admin only)
  const registerUser = async (userData) => {
    return await authService.registerUser(userData);
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    setCurrentTenant(null);
    // After logout, redirect to login page
    navigate('/login', { replace: true });
  };

  // Context value
  const value = {
    currentUser,
    currentTenant,
    login,
    googleLogin,
    registerTenant,
    registerUser,
    logout,
    isAdmin: currentUser?.role === 'admin',
    isAuthenticated: !!currentUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
